import React from 'react';
import { C } from '../constants';
import { ofertaVisita, checkoutVisita, listarMediosPago, urlDeCheckout } from '../services/pagos';
import MedioAnclado from './MedioAnclado';
import PagoQr from './PagoQr';
import { claveVisita, qrVivo, guardarQrVivo, mensajeQrVivo } from '../services/qr-vivo';

/**
 * VISITA TÉCNICA DE VALIDACIÓN — el único cobro del cotizador.
 *
 * El cliente cotiza gratis; lo que paga es que un técnico vaya al sitio y valide que el
 * sistema propuesto se puede instalar como está diseñado. Y esa plata tiene dos destinos
 * posibles, que se le dicen ANTES de pagar:
 *   · si el negocio se cierra → se le descuenta de la instalación;
 *   · si no hay instalación → queda como ingreso del técnico que fue al sitio.
 *
 * 🔢 Las cifras NO se calculan acá. El desglose (valor, 20% de reinversión, neto del técnico,
 * crédito a la obra) sale del hub-api: un número que se muestra en pantalla y además se
 * liquida en el backend tiene que salir de un solo lado, o el día que cambie el canon habrá
 * dos verdades — el mismo defecto que ya costó el PR del diagrama de pérdidas.
 */

const fmtCOP = (n) => `$${Math.round(Number(n) || 0).toLocaleString('es-CO')}`;

export default function VisitaTecnica({ quoteId, email, nombre, telefono, onPagada }) {
  const [oferta, setOferta] = React.useState(null);
  const [medios, setMedios] = React.useState([]);
  const [medio, setMedio] = React.useState('');
  const [documento, setDocumento] = React.useState('');
  const [manual, setManual] = React.useState(null);
  const [err, setErr] = React.useState('');
  const [yendo, setYendo] = React.useState(false);

  React.useEffect(() => {
    let vivo = true;
    ofertaVisita().then((r) => { if (vivo) setOferta(r || null); }).catch(() => {});
    listarMediosPago().then((r) => {
      if (!vivo) return;
      // El QR no va en este selector: es «Otros medios». El QR se ofrece primero en `PagoQr`.
      const lista = (r?.medios || []).filter((m) => m.habilitado && m.tipo !== 'qr');
      setMedios(lista);
      const primero = lista.find((m) => m.listo) || lista[0];
      if (primero) setMedio(primero.clave);
    }).catch(() => {});
    return () => { vivo = false; };
  }, []);

  // QR Bancolombia PRIMERO (dueño, 13-oct-2026): «Pagar» abre el selector; «Otros medios» sigue
  // por el checkout de siempre con el medio elegido abajo.
  const [eligiendo, setEligiendo] = React.useState(false);
  const pagar = async (conQr = false) => {
    // 🔴 Un QR vivo de esta misma visita: se retoma (si se pide QR) o se frena (si se pide otro medio).
    const clave = claveVisita(quoteId);
    const vivo = qrVivo(clave);
    if (vivo) {
      if (conQr) return { ref: vivo.ref, vence: vivo.vence };
      setErr(mensajeQrVivo(vivo));
      return undefined;
    }
    setErr(''); setManual(null); setYendo(true);
    try {
      const retorno = `${window.location.origin}${window.location.pathname}?pago=ok`;
      const r = await checkoutVisita({
        quote_id: quoteId, email, nombre, telefono, documento, retorno_url: retorno,
        ...(conQr ? { qr: true, acepto_terminos: true } : { medio }),
      });
      if (conQr) {
        if (r?.checkout?.tipo !== 'qr') return { error: 'No se pudo generar el QR. Paga con otro medio.' };
        const ref = r.referencia || r.pedido_id;
        guardarQrVivo(clave, ref, r.vence);
        return { ref, vence: r.vence };
      }
      if (r?.checkout?.tipo === 'manual') {
        setManual({ ...r.checkout, referencia: r.referencia });
        if (onPagada) onPagada(r);
        return;
      }
      const url = urlDeCheckout(r?.checkout, `${retorno}&ref=${encodeURIComponent(r.referencia)}`);
      if (url) { window.location.href = url; return; }
      setErr('La pasarela no devolvió una URL de cobro.');
    } catch (e) {
      if (conQr) return { error: (e && e.data && e.data.error) || (e && e.message) || 'No se pudo generar el QR. Paga con otro medio.' };
      if (e && e.status === 409) { setErr((e.data && e.data.error) || e.message); return undefined; }
      // El 422 de precio no fijado no es un error del cliente: es que el dueño no puso la
      // cifra. Se dice tal cual en vez de un «no se pudo» que no dice qué arreglar.
      setErr((e && e.message) || 'No se pudo iniciar el cobro de la visita.');
    } finally {
      setYendo(false);
    }
  };

  if (!oferta) return null;

  if (!oferta.fijado) {
    return (
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.amber}`, borderRadius: 10, padding: '14px 16px', marginTop: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: C.heading }}>Visita técnica de validación</div>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 4, lineHeight: 1.5 }}>
          Un técnico va al sitio y valida que el sistema se pueda instalar como está diseñado.
          El valor todavía no está publicado — escríbenos y te lo confirmamos.
        </div>
      </div>
    );
  }

  const listo = Boolean(documento.trim()) && !yendo;

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.teal}`, borderRadius: 10, padding: '14px 16px', marginTop: 14 }}>
      <PagoQr
        abierto={eligiendo}
        onOtrosMedios={() => { void pagar(false); }}
        onGenerarQr={() => pagar(true)}
        onPagado={() => { if (onPagada) onPagada({ pagada: true }); }}
        onCerrar={() => setEligiendo(false)}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: C.heading }}>Visita técnica de validación</div>
        <div style={{ fontSize: 18, fontWeight: 800, color: C.teal, fontVariantNumeric: 'tabular-nums' }}>{fmtCOP(oferta.valor)}</div>
      </div>

      <ul style={{ margin: '8px 0 10px', paddingLeft: 18, fontSize: 12, color: C.text, lineHeight: 1.55 }}>
        {oferta.reglas.map((t) => <li key={t}>{t}</li>)}
      </ul>

      <div style={{ fontSize: 11.5, color: C.muted, background: `${C.teal}10`, border: `1px solid ${C.teal}33`, borderRadius: 8, padding: '8px 10px', lineHeight: 1.5 }}>
        Si contratas la instalación, estos <strong style={{ color: C.heading }}>{fmtCOP(oferta.creditoInstalacion)}</strong> se
        descuentan del valor de la obra. Si decides no instalar, quedan para el técnico que hizo
        la visita ({fmtCOP(oferta.netoTecnico)} netos; el {oferta.cargoPct}% restante se reinvierte
        en la plataforma).
      </div>

      <label style={{ display: 'block', fontSize: 10.5, letterSpacing: '.08em', textTransform: 'uppercase', color: C.muted, margin: '12px 0 4px' }}>
        Cédula / NIT · obligatorio para la factura
      </label>
      <input value={documento} onChange={(e) => setDocumento(e.target.value)} placeholder="1098765432"
        style={{ width: '100%', background: 'transparent', color: 'inherit', border: `1px solid ${C.border}`, borderRadius: 6, padding: '8px 10px', fontSize: 13 }} />

      {medios.length > 0 && (
        <select value={medio} onChange={(e) => setMedio(e.target.value)}
          style={{ width: '100%', marginTop: 8, background: 'transparent', color: 'inherit', border: `1px solid ${C.border}`, borderRadius: 6, padding: '8px 10px', fontSize: 13 }}>
          {medios.map((m) => (
            <option key={m.clave} value={m.clave} disabled={!m.listo}>
              {m.nombre}{m.listo ? '' : ' · sin llaves'}
            </option>
          ))}
        </select>
      )}

      {/* Mismo medio anclado que la Tienda: si lo guarda, la instalación se le cobra después
          sin pedirle la tarjeta de nuevo. */}
      {email && <MedioAnclado email={email} />}

      {manual && (
        <div style={{ fontSize: 12, color: C.text, marginTop: 10, lineHeight: 1.45 }}>
          {manual.datos?.llave
            ? <>Paga por Bre-B a la llave <strong>{manual.datos.llave}</strong> con la referencia <strong>{manual.referencia}</strong>.</>
            : <>Transfiere a {manual.datos?.banco} {manual.datos?.numero} con la referencia <strong>{manual.referencia}</strong>.</>}
        </div>
      )}
      {err && <div style={{ fontSize: 12, color: C.orange, marginTop: 8 }}>{err}</div>}

      <button type="button" onClick={() => setEligiendo(true)} disabled={!listo}
        style={{
          width: '100%', marginTop: 10, background: listo ? C.teal : C.card2,
          color: listo ? C.heading : C.muted, border: 0, borderRadius: 8, padding: 13,
          fontWeight: 800, fontSize: 12.5, letterSpacing: '.06em', textTransform: 'uppercase',
          cursor: listo ? 'pointer' : 'default',
        }}>
        {yendo ? 'Abriendo…' : (documento.trim() ? 'Agendar y pagar la visita' : 'Falta cédula / NIT')}
      </button>
    </div>
  );
}
